/**
 * Design Collaboration Adapter Helpers
 *
 * Shared protocol helpers for typed design collaboration adapters.
 *
 * Primary responsibilities:
 * - Normalize awareness and bootstrap payloads
 * - Create typed collaboration envelopes and idle state
 *
 * Dependencies:
 * - Design collaboration types
 */

import type { Json } from '@/supabase/types'
import type {
  DesignAwarenessEnvelope,
  DesignAwarenessState,
  DesignBroadcastMessage,
  DesignCollaborationBootstrapResponse,
  DesignCollaborationSession,
  DesignCollaborationState,
  DesignOperationEnvelope,
  DesignResyncRequestEnvelope,
  DesignSnapshotEnvelope
} from '@/lib/spaces/design-collaboration/types'

const DESIGN_PROTOCOL_VERSION = 1 as const

// --------------

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

// --------------

function normalizeStringArray(value: unknown) {
  if (!Array.isArray(value)) {
    return [] as string[]
  }

  return value.filter(
    (entry): entry is string => typeof entry === 'string' && entry.length > 0
  )
}

// --------------

function normalizeJsonObject(value: unknown) {
  if (!isRecord(value)) {
    return undefined
  }

  return value as Record<string, Json | undefined>
}

// --------------

export function normalizeDesignAwarenessState(
  value?: Partial<DesignAwarenessState> | null
): DesignAwarenessState {
  if (!value) {
    return {
      selectedElementIds: []
    }
  }

  return {
    activeTool:
      typeof value.activeTool === 'string' || value.activeTool === null
        ? value.activeTool
        : undefined,
    cursor:
      isRecord(value.cursor) &&
      typeof value.cursor.x === 'number' &&
      typeof value.cursor.y === 'number'
        ? {
            x: value.cursor.x,
            y: value.cursor.y,
            pageId:
              typeof value.cursor.pageId === 'string' ||
              value.cursor.pageId === null
                ? value.cursor.pageId
                : undefined
          }
        : null,
    editingElementId:
      typeof value.editingElementId === 'string' ||
      value.editingElementId === null
        ? value.editingElementId
        : undefined,
    hoveredElementId:
      typeof value.hoveredElementId === 'string' ||
      value.hoveredElementId === null
        ? value.hoveredElementId
        : undefined,
    metadata: normalizeJsonObject(value.metadata),
    pointerDown:
      typeof value.pointerDown === 'boolean'
        ? value.pointerDown
        : undefined,
    selectedElementIds: normalizeStringArray(value.selectedElementIds),
    viewport:
      isRecord(value.viewport) &&
      typeof value.viewport.x === 'number' &&
      typeof value.viewport.y === 'number' &&
      typeof value.viewport.zoom === 'number'
        ? {
            x: value.viewport.x,
            y: value.viewport.y,
            zoom: value.viewport.zoom,
            pageId:
              typeof value.viewport.pageId === 'string' ||
              value.viewport.pageId === null
                ? value.viewport.pageId
                : undefined
          }
        : null
  }
}

// --------------

export function createIdleDesignCollaborationState(): DesignCollaborationState {
  return {
    awarenessBySession: {},
    connectedAt: null,
    lastError: null,
    lastOperation: null,
    lastResyncRequest: null,
    lastSnapshot: null,
    participants: [],
    session: null,
    status: 'idle'
  }
}

// --------------

export function createBootstrappingDesignCollaborationState(): DesignCollaborationState {
  return {
    ...createIdleDesignCollaborationState(),
    status: 'bootstrapping'
  }
}

// --------------

export function createDesignCollaborationStateFromSession(
  session: DesignCollaborationSession
): DesignCollaborationState {
  return {
    awarenessBySession: {},
    connectedAt: null,
    lastError: null,
    lastOperation: null,
    lastResyncRequest: null,
    lastSnapshot: null,
    participants: [session.participant],
    session,
    status: 'connecting'
  }
}

// --------------

export function createDesignOperationEnvelope(input: {
  designId: string
  id?: string
  originSessionId: string
  payload: Json
  type: string
  userId: string
  version?: number | null
  createdAt?: string
}): DesignOperationEnvelope {
  return {
    createdAt: input.createdAt || new Date().toISOString(),
    designId: input.designId,
    id: input.id || crypto.randomUUID(),
    originSessionId: input.originSessionId,
    payload: input.payload,
    type: input.type,
    userId: input.userId,
    version: input.version ?? null
  }
}

// --------------

export function createDesignAwarenessEnvelope(input: {
  awareness: DesignAwarenessState
  createdAt?: string
  designId: string
  originSessionId: string
  userId: string
}): DesignAwarenessEnvelope {
  return {
    awareness: normalizeDesignAwarenessState(input.awareness),
    createdAt: input.createdAt || new Date().toISOString(),
    designId: input.designId,
    originSessionId: input.originSessionId,
    userId: input.userId
  }
}

// --------------

export function createDesignSnapshotEnvelope(input: {
  designId: string
  id?: string
  originSessionId: string
  snapshot: Json
  userId: string
  version?: number | null
}): DesignSnapshotEnvelope {
  return {
    createdAt: new Date().toISOString(),
    designId: input.designId,
    id: input.id || crypto.randomUUID(),
    originSessionId: input.originSessionId,
    snapshot: input.snapshot,
    userId: input.userId,
    version: input.version ?? null
  }
}

// --------------

export function createDesignResyncRequestEnvelope(input: {
  designId: string
  knownVersion?: number | null
  originSessionId: string
  reason?: string
  userId: string
}): DesignResyncRequestEnvelope {
  return {
    createdAt: new Date().toISOString(),
    designId: input.designId,
    id: crypto.randomUUID(),
    knownVersion: input.knownVersion ?? null,
    originSessionId: input.originSessionId,
    reason: input.reason || null,
    userId: input.userId
  }
}

// --------------

export function isDesignBroadcastMessage(
  value: unknown
): value is DesignBroadcastMessage {
  if (!isRecord(value) || value.protocolVersion !== DESIGN_PROTOCOL_VERSION) {
    return false
  }

  if (value.kind === 'awareness') {
    return isRecord(value.awareness)
  }

  if (value.kind === 'operation') {
    return isRecord(value.operation)
  }

  if (value.kind === 'snapshot') {
    return isRecord(value.snapshot)
  }

  if (value.kind === 'resync-request') {
    return isRecord(value.request)
  }

  return false
}

// --------------

export function parseDesignCollaborationBootstrapResponse(
  value: unknown
): DesignCollaborationBootstrapResponse {
  if (!isRecord(value) || !isRecord(value.collaboration)) {
    throw new Error('Invalid collaboration bootstrap response')
  }

  const collaboration = value.collaboration

  if (
    typeof collaboration.spaceId !== 'string' ||
    typeof collaboration.designId !== 'string' ||
    typeof collaboration.channelName !== 'string'
  ) {
    throw new Error('Missing collaboration identifiers')
  }

  return value as DesignCollaborationBootstrapResponse
}
